import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Tag,
  MapPin,
  CheckCircle,
  AlertCircle,
  ShoppingCart,
  Calendar,
  DollarSign,
  Building2,
  FileText,
  CreditCard,
  Upload,
  Copy,
  Check
} from 'lucide-react';
import Card from '../modern-components/ui/Card';
import Button from '../modern-components/ui/Button';
import Badge from '../modern-components/ui/Badge';
import { quoteAPI, orderAPI } from '../services/api';

const PlaceOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const responseId = searchParams.get('response');

  const [quote, setQuote] = useState(null);
  const [response, setResponse] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Shipping details
  const [shippingDetails, setShippingDetails] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_pincode: ''
  });

  // Payment fields
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);

  useEffect(() => {
    if (responseId) {
      fetchQuoteDetails();
      fetchBankDetails();
    } else {
      setError('No quote response selected');
      setLoading(false);
    }
  }, [responseId]);

  const fetchBankDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/bank-details/active`);
      const data = await response.json();
      if (data.success) {
        setBankDetails(data.data);
      }
    } catch (err) {
      console.error('Error fetching bank details:', err);
    }
  };

  const fetchQuoteDetails = async () => {
    try {
      // We need to find the quote from the quotes list that contains this response
      const quotesResponse = await quoteAPI.getQuotes();
      if (quotesResponse.data.success) {
        const quotes = quotesResponse.data.data;
        let foundQuote = null;
        let foundResponse = null;

        for (const q of quotes) {
          if (q.responses && q.responses.length > 0) {
            const resp = q.responses.find(r => r.id === parseInt(responseId));
            if (resp) {
              foundQuote = q;
              foundResponse = resp;
              break;
            }
          }
        }

        if (foundQuote && foundResponse) {
          setQuote(foundQuote);
          setResponse(foundResponse);
          // Pre-fill delivery address from customer profile if available
          const user = JSON.parse(localStorage.getItem('user'));
          if (user && user.customer && user.customer.address) {
            setDeliveryAddress(user.customer.address);
          }
        } else {
          setError('Quote response not found');
        }
      }
    } catch (err) {
      setError('Failed to fetch quote details');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Validate shipping details
    if (!shippingDetails.customer_name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!shippingDetails.customer_email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!shippingDetails.customer_phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (!shippingDetails.shipping_address.trim()) {
      setError('Please enter shipping address');
      return;
    }
    if (!shippingDetails.shipping_city.trim()) {
      setError('Please enter city');
      return;
    }
    if (!shippingDetails.shipping_state.trim()) {
      setError('Please enter state');
      return;
    }
    if (!shippingDetails.shipping_pincode.trim()) {
      setError('Please enter pincode');
      return;
    }

    // Validate payment fields
    if (!transactionId.trim()) {
      setError('Please enter transaction ID');
      return;
    }
    if (!paymentProof) {
      setError('Please upload payment proof (screenshot/PDF)');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('quote_response_id', parseInt(responseId));
      formData.append('delivery_address', deliveryAddress);
      formData.append('payment_method', paymentMethod);

      // Append shipping details
      formData.append('customer_name', shippingDetails.customer_name);
      formData.append('customer_email', shippingDetails.customer_email);
      formData.append('customer_phone', shippingDetails.customer_phone);
      formData.append('shipping_address', shippingDetails.shipping_address);
      formData.append('shipping_city', shippingDetails.shipping_city);
      formData.append('shipping_state', shippingDetails.shipping_state);
      formData.append('shipping_pincode', shippingDetails.shipping_pincode);

      // Append payment details
      formData.append('transaction_id', transactionId);
      formData.append('payment_proof', paymentProof);

      const orderResponse = await orderAPI.createOrder(formData);

      if (orderResponse.data.success) {
        setSuccess('Order placed successfully!');
        setTimeout(() => {
          navigate('/dashboard?tab=orders');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Order Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard?tab=quotes')}>
            Back to Quotes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate('/dashboard?tab=quotes')}
          className="mb-6 animate-fade-in"
        >
          Back to Quotes
        </Button>

        {/* Page Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Place Order
          </h1>
          <p className="text-lg text-gray-600">
            Review your order details and confirm delivery address
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-slide-up">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-900">Success!</h3>
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {error && quote && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-slide-up">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {quote && response && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Order Details */}
            <div className="space-y-6">
              {/* Product Details Card */}
              <Card className="animate-slide-up">
                <div className="flex items-center space-x-2 mb-4">
                  <Package className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Product Details
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Badge variant="gray" className="mb-2">
                      {quote.product?.category?.name || 'Category'}
                    </Badge>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {quote.product?.name}
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Quantity</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {quote.quantity} {quote.unit}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Quote Number</p>
                      <p className="text-lg font-semibold text-gray-900">
                        #{quote.quote_number}
                      </p>
                    </div>
                  </div>

                  {quote.customer_notes && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-1">Your Notes</p>
                      <p className="text-gray-700">
                        {quote.customer_notes}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Pricing Details Card */}
              <Card className="animate-slide-up bg-gradient-to-br from-primary-50 to-secondary-50 border-2 border-primary-100">
                <div className="flex items-center space-x-2 mb-4">
                  <DollarSign className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Pricing Details
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Supplier</span>
                    <span className="font-semibold text-gray-900">
                      {response.quoted_by === 'admin'
                        ? 'Loha Mart'
                        : response.supplier?.company_name || 'Supplier'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-primary-200">
                    <span className="text-gray-600">Price per Unit</span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(response.price_per_unit)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Quantity</span>
                    <span className="font-semibold text-gray-900">
                      {quote.quantity} {quote.unit}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t-2 border-primary-300">
                    <span className="text-lg font-bold text-gray-900">Total Amount</span>
                    <span className="text-3xl font-bold text-primary-600">
                      {formatPrice(response.total_price)}
                    </span>
                  </div>

                  {response.remarks && (
                    <div className="pt-4 border-t border-primary-200">
                      <p className="text-sm text-gray-500 mb-1">Remarks</p>
                      <p className="text-gray-700">{response.remarks}</p>
                    </div>
                  )}

                  <div className="pt-4 border-t border-primary-200">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Valid for {response.validity_days || 30} days</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column - Delivery & Action */}
            <div className="space-y-6">
              {/* Shipping Details Card */}
              <Card className="animate-fade-in">
                <div className="flex items-center space-x-2 mb-4">
                  <MapPin className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Shipping Details
                  </h2>
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={shippingDetails.customer_name}
                      onChange={(e) => setShippingDetails({...shippingDetails, customer_name: e.target.value})}
                      placeholder="Enter your full name"
                      disabled={submitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={shippingDetails.customer_email}
                        onChange={(e) => setShippingDetails({...shippingDetails, customer_email: e.target.value})}
                        placeholder="your.email@example.com"
                        disabled={submitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={shippingDetails.customer_phone}
                        onChange={(e) => setShippingDetails({...shippingDetails, customer_phone: e.target.value})}
                        placeholder="10-digit mobile number"
                        maxLength="10"
                        disabled={submitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Complete Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      value={shippingDetails.shipping_address}
                      onChange={(e) => setShippingDetails({...shippingDetails, shipping_address: e.target.value})}
                      placeholder="House/Flat No., Building Name, Street, Area..."
                      disabled={submitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:bg-gray-100"
                    />
                  </div>

                  {/* City, State, Pincode */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={shippingDetails.shipping_city}
                        onChange={(e) => setShippingDetails({...shippingDetails, shipping_city: e.target.value})}
                        placeholder="City"
                        disabled={submitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={shippingDetails.shipping_state}
                        onChange={(e) => setShippingDetails({...shippingDetails, shipping_state: e.target.value})}
                        placeholder="State"
                        disabled={submitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={shippingDetails.shipping_pincode}
                        onChange={(e) => setShippingDetails({...shippingDetails, shipping_pincode: e.target.value})}
                        placeholder="6-digit pincode"
                        maxLength="6"
                        disabled={submitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Payment Details Card */}
              <Card className="animate-fade-in">
                <div className="flex items-center space-x-2 mb-4">
                  <CreditCard className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Payment Details
                  </h2>
                </div>

                <div className="space-y-4">
                  {/* Payment Method Display */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 font-medium">
                      Bank Transfer
                    </div>
                  </div>

                  {/* Bank Details Display */}
                  {bankDetails && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h3 className="text-sm font-bold text-blue-900 mb-3">Pay to Account</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Account Holder:</span>
                          <span className="font-semibold text-gray-900">{bankDetails.account_holder_name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Bank Name:</span>
                          <span className="font-semibold text-gray-900">{bankDetails.bank_name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Account Number:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900">{bankDetails.account_number}</span>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText(bankDetails.account_number)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">IFSC Code:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900">{bankDetails.ifsc_code}</span>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText(bankDetails.ifsc_code)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {bankDetails.branch && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Branch:</span>
                            <span className="font-semibold text-gray-900">{bankDetails.branch}</span>
                          </div>
                        )}
                        {bankDetails.upi_id && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">UPI ID:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-gray-900">{bankDetails.upi_id}</span>
                              <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(bankDetails.upi_id)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Transaction ID Input - Show only for non-COD */}
                  {paymentMethod !== 'cod' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Transaction ID / Reference Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                          placeholder="Enter UPI/Bank transaction ID"
                          disabled={submitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                        />
                      </div>

                      {/* Payment Proof Upload */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Payment Proof (Screenshot/PDF) <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer hover:border-primary-500 hover:bg-gray-50 transition-colors">
                            <div className="text-center">
                              <Upload className="mx-auto h-8 w-8 text-gray-400" />
                              <div className="mt-2 text-sm text-gray-600">
                                {paymentProof ? (
                                  <span className="text-green-600 font-semibold">
                                    <Check className="w-4 h-4 inline mr-1" />
                                    {paymentProof.name}
                                  </span>
                                ) : (
                                  <>
                                    <span className="font-semibold text-primary-600">Click to upload</span>
                                    <span className="text-gray-500"> or drag and drop</span>
                                  </>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                PNG, JPG, or PDF (max 10MB)
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => setPaymentProof(e.target.files[0])}
                              disabled={submitting}
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* Order Summary Card */}
              <Card className="animate-fade-in bg-gray-50 border-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Order Summary
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Product</span>
                    <span className="font-semibold text-gray-900">{quote.product?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Quantity</span>
                    <span className="font-semibold text-gray-900">{quote.quantity} {quote.unit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Unit Price</span>
                    <span className="font-semibold text-gray-900">{formatPrice(response.price_per_unit)}</span>
                  </div>
                  <div className="pt-3 border-t-2 border-gray-300">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-900">Order Total</span>
                      <span className="text-2xl font-bold text-primary-600">
                        {formatPrice(response.total_price)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard?tab=quotes')}
                  disabled={submitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  leftIcon={submitting ? null : <ShoppingCart className="w-5 h-5" />}
                  onClick={handlePlaceOrder}
                  disabled={
                    submitting ||
                    !shippingDetails.customer_name.trim() ||
                    !shippingDetails.customer_email.trim() ||
                    !shippingDetails.customer_phone.trim() ||
                    !shippingDetails.shipping_address.trim() ||
                    !shippingDetails.shipping_city.trim() ||
                    !shippingDetails.shipping_state.trim() ||
                    !shippingDetails.shipping_pincode.trim() ||
                    (paymentMethod !== 'cod' && (!transactionId.trim() || !paymentProof))
                  }
                  className="flex-1"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Placing Order...
                    </>
                  ) : (
                    'Confirm & Place Order'
                  )}
                </Button>
              </div>

              {/* Trust Badge */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 animate-fade-in">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">
                      Secure Order
                    </h4>
                    <p className="text-xs text-blue-700">
                      Your order is secure and will be processed immediately. You'll receive confirmation and tracking details via email.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaceOrder;
